import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui';
import type { LLMConfig } from '../../types/llm-config';

interface DeleteLLMDialogProps {
  llm: LLMConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => void;
  isPending?: boolean;
}

export const DeleteLLMDialog: React.FC<DeleteLLMDialogProps> = ({
  llm,
  open,
  onOpenChange,
  onDelete,
  isPending = false,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete LLM Configuration</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the LLM configuration for{' '}
            <span className="font-semibold">{llm.displayName}</span>{' '}
            (<span className="font-mono text-sm">{llm.modelId}</span>)?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 space-y-2">
          <p className="text-sm font-medium text-destructive">Warning:</p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>This action cannot be undone</li>
            <li>Any applications or features using this model may be affected</li>
            <li>Historical data referencing this model will remain but the configuration will be removed</li>
          </ul>
        </div>

        <div className="rounded-lg border bg-muted p-4 space-y-1 text-xs">
          <p><span className="font-medium">Model ID:</span> {llm.modelId}</p>
          <p><span className="font-medium">Provider:</span> {llm.provider}</p>
          <p><span className="font-medium">Status:</span> {llm.isActive ? 'Active' : 'Inactive'}</p>
          <p><span className="font-medium">Created:</span> {new Date(llm.createdAt).toLocaleString()}</p>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? 'Deleting...' : 'Delete Configuration'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
